import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Routes, Route } from "react-router-dom";
import { GlitzProvider } from "@glitz/react";
import { styled } from "@glitz/react";
import glitz from "./glitzSetup";
import StartPage from "./pages/StartPage";
import ProductPage from "./pages/ProductPage";
import CategoryPage from "./pages/CategoryPage";
import BrandPage from "./pages/BrandPage";
import CheckoutPage from "./pages/CheckoutPage";
import OrderPayPage from "./pages/OrderPayPage";
import SearchPage from "./pages/SearchPage";
import PackagePage from "./pages/PackagePage";
import PackagesPage from "./pages/PackagesPage";
import EnergyCalculatorPage from "./pages/EnergyCalculatorPage";
import TrackingPage from "./pages/TrackingPage";
import TermsPage from "./pages/TermsPage";
import TopMenu from "./components/TopMenu";
import Footer from "./components/Footer";
import NewsletterModal from "./components/NewsletterModal";
import { initCookieConsentSync, hasAnalyticsConsent } from "./lib/cookieConsent";
import { trackMetaPageView } from "./lib/metaPixel";

const AppMain = styled.main({
  flex: { grow: 1, shrink: 1, basis: 0 },
  display: "flex",
  flexDirection: "column",
});

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
    gtag?: (...args: unknown[]) => void;
  }
}

const GA4_MEASUREMENT_ID = "G-P7TGGP4R9W";

const App = () => {
  const location = useLocation();
  const isCheckoutPage =
    location.pathname.startsWith("/checkout") ||
    location.pathname.startsWith("/order") ||
    location.pathname.startsWith("/track");

  useEffect(() => {
    return initCookieConsentSync();
  }, []);

  // SPA page_view vid varje route-byte. GA4 (gtag) är konfigurerat med
  // send_page_view:false i index.html, så vi skickar både första laddningen
  // och efterföljande navigering härifrån. dataLayer-push:en täcker GTM-sidan.
  useEffect(() => {
    const basename = process.env.PUBLIC_URL || "";
    const path = `${basename}${location.pathname}${location.search}`;
    const pageLocation = window.location.href;
    const pageTitle = document.title;

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "page_view",
      page_path: path,
      page_location: pageLocation,
      page_title: pageTitle,
    });

    if (hasAnalyticsConsent() && typeof window.gtag === "function") {
      window.gtag("event", "page_view", {
        send_to: GA4_MEASUREMENT_ID,
        page_path: path,
        page_location: pageLocation,
        page_title: pageTitle,
      });
    }
    trackMetaPageView();
  }, [location.pathname, location.search]);

  return (
    <GlitzProvider glitz={glitz}>
      {!isCheckoutPage && <TopMenu />}
      <AppMain>
        <Routes>
          <Route path="/" element={<StartPage />} />
          {/*
            Med PUBLIC_URL=/shop (basename "/shop") är URL i webbläsaren …/shop/product/slug
            men React Routers path är bara /product/:slug — INTE /shop/product/…
          */}
          <Route path="/product/:id" element={<ProductPage />} />
          <Route path="/product/:id/" element={<ProductPage />} />
          <Route path="/kategorier/:main/:sub?" element={<CategoryPage />} />
          <Route path="/varumarken/:brand" element={<BrandPage />} />
          <Route path="/paket" element={<PackagesPage />} />
          <Route path="/paket/:slug" element={<PackagePage />} />
          <Route path="/energi-kalkylator" element={<EnergyCalculatorPage />} />
          <Route path="/sok" element={<SearchPage />} />
          <Route path="/villkor" element={<TermsPage />} />
          <Route path="/checkout" element={<CheckoutPage />} />
          <Route path="/checkout/pay/:orderId" element={<CheckoutPage />} />
          <Route path="/order/:orderRef" element={<OrderPayPage />} />
          <Route path="/track/:orderRef" element={<TrackingPage />} />
        </Routes>
      </AppMain>
      <Footer />
      <NewsletterModal />
    </GlitzProvider>
  );
};

export default App;
